/**
 * TextSlotControl — Textarea for text slot editing.
 *
 * Works with full SlotValue objects, preserving styleClass, color,
 * appearance, translations, and other metadata on change.
 * V1: plain textarea. Tiptap rich text deferred.
 */

import { useState, useEffect, useRef } from 'react'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { SlotValue } from '@zaviago/storefront-config'

interface TextSlotControlProps {
  slotValue: SlotValue
  onChange: (v: SlotValue) => void
  label: string
}

function extractText(slot: SlotValue): string {
  if (typeof slot === 'string') return slot
  if ('value' in slot && typeof slot.value === 'string') return slot.value
  if ('label' in slot) return slot.label
  return ''
}

export function TextSlotControl({ slotValue, onChange, label }: TextSlotControlProps) {
  const [local, setLocal] = useState(() => extractText(slotValue))
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    setLocal(extractText(slotValue))
  }, [slotValue])

  const handleChange = (next: string) => {
    setLocal(next)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      // Reconstruct full SlotValue preserving metadata
      if (typeof slotValue === 'string') {
        onChange(next)
      } else if ('value' in slotValue) {
        onChange({ ...slotValue, value: next })
      } else {
        onChange({ value: next })
      }
    }, 300)
  }

  useEffect(() => {
    return () => clearTimeout(timerRef.current)
  }, [])

  return (
    <div className="space-y-1.5">
      <Label className="text-[13px] font-medium text-gray-600">{label}</Label>
      <Textarea
        value={local}
        onChange={(e) => handleChange(e.target.value)}
        rows={3}
        className="resize-y"
      />
    </div>
  )
}
